import { Injectable, WritableSignal, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UserAccessService } from '@services/user-access/user-access.service';
import { NavigationHelper } from './navigation.helper';
import { LocalStorageService } from '@services/local-storage-service/local-storage.service';
import { NavigationDataService } from '../../services-api/navigation-data/navigation-data.service';
import { Country, Device, Region, Site } from '../../models/navigation.interface';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private userAccessService = inject(UserAccessService);
  private router = inject(Router);
  private localStorageService = inject(LocalStorageService);
  private showModuleMap: Map<string, boolean> = new Map<string, boolean>();
  private lockModuleMap: Map<string[], boolean> = new Map<string[], boolean>();
  private navigationDataService = inject(NavigationDataService);
  moduleIdSig = signal<string>(NavigationHelper.navigationDefault.module);
  levelSig = computed<WritableSignal<string>>(() => signal(NavigationHelper.navigationDefault.level));
  newPortfolioListSig = this.navigationDataService.getPortfolioList();
  portfolioListSignal = computed(() => {
    return this.newPortfolioListSig();
  });

  selectedPortfolioSig = computed(() => {
    const selectedPortfolioId =
      this.portfolioIdSig() ||
      this.localStorageService.selectedPortfolioIdLocalSignal() ||
      this.portfolioListSignal()?.[0]?._id;
    const selectedPortfolio = this.portfolioListSignal()?.find(item => item?._id === selectedPortfolioId);
    const portfolio = selectedPortfolio || this.portfolioListSignal()?.[0];
    return portfolio;
  });

  idSig = signal<string>('');
  siteIdSig = signal<string>('');
  deviceIdSig = signal<string>('');
  countryListSignal = computed(() => signal(this.selectedPortfolioSig()?.countries));
  selectedCountrySig = computed<WritableSignal<Country | undefined>>(() => {
    const countries = this.selectedPortfolioSig()?.countries || [];
    const countryId = this.idSig();
    const siteId = this.siteIdSig();
    const deviceId = this.deviceIdSig();
    const regionId = this.regionIdSig();
    let country: Country | undefined;

    if (countryId) {
      country = countries.find((country: Country) => country._id === countryId);
    }
    if (!country && regionId) {
      country = countries.find((country: Country) =>
        country.regions?.some((region: Region) => region._id === regionId),
      );
    }
    if (!country && siteId) {
      country = countries.find((country: Country) =>
        country.sites?.some((countrySite: Site) => countrySite._id === siteId),
      );
    }
    if (!country && deviceId) {
      const site = this.selectedPortfolioSig()?.sites.find((site: Site) =>
        site.Devices.some((device: Device) => device._id === deviceId),
      );
      if (site) {
        country = countries.find((country: Country) =>
          country.sites?.some((countrySite: Site) => countrySite._id === site._id),
        );
      }
    }
    if (!country) {
      country = countries[0];
    }
    return signal(country);
  });

  regionIdSig = signal<string>('');

  selectedRegion = computed<Region | null>(() => {
    const regionId = this.regionIdSig();
    const siteId = this.siteIdSig();
    const deviceId = this.deviceIdSig();
    const country = this.selectedCountry();
    if (!country?.regions?.length) return null;

    // If explicit regionId is set, use it
    if (regionId) {
      return country.regions.find((r: any) => r._id === regionId) ?? country.regions[0];
    }

    if (siteId || deviceId) {
      const currentSite = this.selectedSite();
      if (currentSite) {
        const siteRegion = country.regions.find((r: any) =>
          r.sites.some((s: any) => s._id === currentSite._id)
        );
        if (siteRegion) return siteRegion;
      }
    }

    // Default to first region
    return country.regions[0];
  });

  selectedSiteSig = computed<WritableSignal<Site | undefined>>(() => {
    const siteId = this.siteIdSig();
    const deviceId = this.deviceIdSig();
    const regionId = this.regionIdSig();
    let site: Site | undefined;

    if (siteId?.trim()) {
      site =
        this.selectedPortfolioSig()?.sites.find(site => site._id === siteId) ||
        this.selectedPortfolioSig()?.sites[0];
    } else if (deviceId?.trim()) {
      site =
        this.selectedPortfolioSig()?.sites.find(site => site.Devices.some(device => device._id === deviceId)) ||
        this.selectedPortfolioSig()?.sites[0];
    } else if (regionId?.trim()) {
      site =
        this.selectedPortfolioSig()?.sites.find(site => site._id_region === regionId) ||
        this.selectedPortfolioSig()?.sites[0];
    } else if (this.idSig()?.trim()) {
      site =
        this.selectedPortfolioSig()?.sites.find(site => site._id_country === this.idSig()) ||
        this.selectedPortfolioSig()?.sites[0];
    } else {
      const fallbackSiteId = this.selectedRegion()?.sites?.[0]?._id || this.selectedCountry()?.sites?.[0]?._id;
      site =
        this.selectedPortfolioSig()?.sites.find(site => site._id === fallbackSiteId) ||
        this.selectedPortfolioSig()?.sites[0];
    }
    return signal(site);
  });

  selectedDeviceSig = computed<WritableSignal<Device>>(() => {
    const deviceId = this.deviceIdSig();
    const device =
      this.selectedSiteSig()()?.Devices?.find(device => device._id === deviceId) || this.selectedSiteSig()()?.Devices?.[0];
    return signal(device);
  });
  selectedCountry = computed<Country>(() => this.selectedCountrySig()());
  selectedSite = computed<Site>(() => this.selectedSiteSig()());
  selectedDevice = computed<Device>(() => this.selectedDeviceSig()());

  filteredCountries = computed<WritableSignal<Country[]>>(() => signal(this.selectedPortfolioSig()?.countries));
  // Filter sites by region if selected, otherwise by country
  filteredSites = computed<WritableSignal<Site[]>>(() => {
    const sites = this.selectedPortfolioSig()?.sites ?? [];
    const region = this.selectedRegion();
    const country = this.selectedCountry();
    if (region) {
      const regionSiteIds = new Set(region.sites.map((s: any) => s._id));
      return signal(sites.filter(s => regionSiteIds.has(s._id)));
    }

    return signal(sites.filter(s => s._id_country === country?._id));
  });

  filteredDevices = computed<WritableSignal<Device[]>>(() => {
    const site = computed<WritableSignal<Site[]>>(() => {
      switch (this.levelSig()()) {
        case 'portfolio':
          return signal(this.selectedPortfolioSig()?.sites);
        case 'country':
          return signal(
            this.selectedPortfolioSig()?.sites?.filter(item => item._id_country === this.selectedCountry()?._id),
          );
        case 'site':
          return signal([this.selectedSiteSig()()]);
        case 'device':
          return signal([this.selectedSiteSig()()]);
        default:
          return signal([]);
      }
    });
    return signal(
      this.selectedPortfolioSig()?.devices.filter(item1 => site()()?.some(item2 => item2?._id === item1?.site)) || [],
    );
  });

  isMonitorInfoDevices = signal(false);
  portfolioIdSig = signal('');

  deviceTypesListSig = this.navigationDataService.getDeviceTypes();
  selectedDeviceType = computed(() => this.deviceTypesListSig()?.[this.selectedDeviceSig()()?._id_deviceType]);
  selectedDeviceTimezoneOffset = computed(() => signal(this.selectedDeviceSig()()?.Timezone?.timezoneOffset));
  
  selectedDeviceInSettings = signal(null);

  constructor() {
    this.subscribeToRouteChange();
  }

  isLocked(roles: string[]): boolean {
    if (this.lockModuleMap.has(roles)) {
      return !!this.lockModuleMap.get(roles);
    }
    const result = !this.userAccessService.canAccess(roles);
    this.lockModuleMap.set(roles, result);
    return result;
  }

  showModule(role: string): boolean | undefined {
    if (this.showModuleMap.has(role)) {
      return this.showModuleMap.get(role);
    }
    const result = this.userAccessService.canAccess([role]);
    this.showModuleMap.set(role, result);
    return result;
  }

  navigateToModule(urlParams: string[], event: Event): void {
    this.router.navigate([...urlParams]);
    if (event) event.preventDefault();
  }

  setSelectedPortfolio(portfolio) {
    if (portfolio) {
      this.localStorageService.setSelectedPortfolioId(`${portfolio._id}`);
      this.idSig.set('');
      this.siteIdSig.set('');
      this.deviceIdSig.set('');
      this.regionIdSig.set('');
    }
  }

  selectPortfolio(id?: string | null): void {
    const portfolioId = id?.trim();
    if (!portfolioId || portfolioId === 'undefined' || portfolioId === 'null') return;

    this.portfolioIdSig.set(portfolioId);
    this.localStorageService.setSelectedPortfolioId(portfolioId);
  }

  setSelectedCountry(country: any) {
    if (country) {
      this.idSig.set(country._id ?? '');
      this.siteIdSig.set('');
      this.deviceIdSig.set('');
      this.regionIdSig.set('');
    }
  }

  setSelectedRegion(region: Region) {
    this.regionIdSig.set(region?._id ?? '');
  }

  setSelectedSite(site) {
    if (site) {
      this.idSig.set('');
      this.siteIdSig.set(site._id ?? '');
      this.deviceIdSig.set('');
      this.regionIdSig.set(site._id_region ?? '');
    }
  }

  setSelectedDevice(device) {
    if (device) {
      this.deviceIdSig.set(device._id ?? '');
    }
  }

  private subscribeToRouteChange(): void {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .pipe(takeUntilDestroyed())
      .subscribe(value => {
        if (value instanceof NavigationEnd) {
          const navItems = `${value.urlAfterRedirects}`.split('/').filter(item => !!item);
          if (this.moduleIdSig() !== navItems[0]) {
            this.moduleIdSig.set(`${navItems[0]}`);
          }
          if (this.levelSig()() !== navItems[1]) {
            this.levelSig().set(navItems[1]);
          }
          const index = navItems.findIndex(item => item === 'portfolio');
          const portfolioId = index !== -1 && index + 1 < navItems.length ? navItems[index + 1] : null;
          this.portfolioIdSig.set(portfolioId);
          if (portfolioId) {
            this.localStorageService.setSelectedPortfolioId(`${portfolioId}`);
          }
          if (navItems.length > 4) {
            const level = navItems[3];
            const id = navItems[4];

            this.levelSig().set(level);
            switch (level) {
              case 'country': {
                this.idSig.set(id);
                this.regionIdSig.set('');
                break;
              }
              case 'region': {
                this.idSig.set('');
                this.regionIdSig.set(id);
                break;
              }
              case 'site': {
                this.idSig.set('');
                this.regionIdSig.set('');
                this.siteIdSig.set(id);
                break;
              }
              case 'device': {
                this.idSig.set('');
                this.regionIdSig.set('');
                this.siteIdSig.set('');
                this.deviceIdSig.set(id);
                break;
              }
            }
          } else {
            this.levelSig().set('portfolio');
          }
        }
      });
  }
}
